/**
 * ABOUTME: Centralized message template system for TradesPurple
 * ABOUTME: Provides consistent messaging across all quote request flows
 */

import { debug } from '@/lib/utils';

// Template data types - what data each template requires
export interface BaseTemplateData {
  userName: string;
  postcode: string;
  isFromSavedList?: boolean; // Indicates if contacting from user's saved list
}

export interface SmartQuoteTemplateData extends BaseTemplateData {
  jobType: string;
  jobDetails: string;
}

export interface NotificationTemplateData extends BaseTemplateData {
  jobTitle: string;
  jobDescription: string;
}

export interface QuickContactTemplateData extends BaseTemplateData {
  tradeType: string;
}

// Template types
export type TemplateType = 'smartQuote' | 'notification' | 'quickContact';

// Template definitions
const templates = {
  smartQuote: {
    subject: (data: SmartQuoteTemplateData) => {
      if (data.isFromSavedList) {
        return `${data.jobType} job in ${data.postcode}`;
      }
      // Add article 'a' or 'an' based on first letter
      const article = /^[aeiou]/i.test(data.jobType) ? 'an' : 'a';
      return `Looking for ${article} ${data.jobType.toLowerCase()}`;
    },
    body: (data: SmartQuoteTemplateData) => {
      if (data.isFromSavedList) {
        return `Hi there,

I'd like to talk to you about a ${data.jobType.toLowerCase()} job in ${data.postcode}.

${data.jobDetails}

Are you available to discuss this? When would suit you?

Thanks,`;
      }
      // Add article 'a' or 'an' based on first letter
      const article = /^[aeiou]/i.test(data.jobType) ? 'an' : 'a';
      return `Hi there,

I'm looking for ${article} ${data.jobType.toLowerCase()} in ${data.postcode} and my neighbours recommended you.

${data.jobDetails}

Are you available to discuss this? When would suit you?

Thanks,`;
    }
  },

  notification: {
    subject: (data: NotificationTemplateData) => `Need help with ${data.jobTitle}`,
    body: (data: NotificationTemplateData) => {
      if (data.isFromSavedList) {
        return `Hi there,

I need your help with ${data.jobTitle.toLowerCase()} in ${data.postcode}.

${data.jobDescription}

Are you available to discuss this? When would suit you?

Thanks,`;
      }
      return `Hi there,

I'm looking for help with ${data.jobTitle.toLowerCase()} in ${data.postcode} and my neighbours recommended you.

${data.jobDescription}

Are you available to discuss this? When would suit you?

Thanks,`;
    }
  },

  quickContact: {
    subject: (data: QuickContactTemplateData) => {
      if (data.isFromSavedList) {
        return `Need your help with a job`;
      }
      return `Looking for ${data.tradeType}`;
    },
    body: (data: QuickContactTemplateData) => {
      if (data.isFromSavedList) {
        return `Hi there,

I'd like to talk to you about a job in ${data.postcode}.

Are you available to discuss this?

Thanks,`;
      }
      return `Hi there,

I'm looking for a ${data.tradeType} near ${data.postcode} and my neighbours recommended you.

Are you available to come and look at a job soon?

Thanks,`;
    }
  }
};

// Placeholder detection regex
const PLACEHOLDER_REGEX = /\[([^\]]+)\]/g;

// Validation function to ensure no placeholders remain
function validateNoPlaceholders(text: string, templateType: string): void {
  const placeholders = text.match(PLACEHOLDER_REGEX);
  if (placeholders && placeholders.length > 0) {
    const placeholderList = placeholders.join(', ');
    debug.error(`Template ${templateType} contains unresolved placeholders: ${placeholderList}`);
    throw new Error(`Message contains unresolved placeholders: ${placeholderList}`);
  }
}

// Main function to generate a filled message
export function generateMessage<T extends BaseTemplateData>(
  templateType: TemplateType,
  data: T
): { subject: string; body: string } {
  // Validate required base fields (userName no longer required since user will add manually)
  if (!data.postcode || data.postcode.trim() === '') {
    throw new Error('Postcode is required');
  }

  // Get the template
  const template = templates[templateType];
  if (!template) {
    throw new Error(`Unknown template type: ${templateType}`);
  }

  // Type-safe template generation
  let subject: string;
  let body: string;

  switch (templateType) {
    case 'smartQuote': {
      const sqData = data as SmartQuoteTemplateData;
      if (!sqData.jobType || !sqData.jobDetails) {
        throw new Error('Smart quote requires jobType and jobDetails');
      }
      subject = templates.smartQuote.subject(sqData);
      body = templates.smartQuote.body(sqData);
      break;
    }

    case 'notification': {
      const notifData = data as NotificationTemplateData;
      if (!notifData.jobTitle || !notifData.jobDescription) {
        throw new Error('Notification requires jobTitle and jobDescription');
      }
      subject = templates.notification.subject(notifData);
      body = templates.notification.body(notifData);
      break;
    }

    case 'quickContact': {
      const qcData = data as QuickContactTemplateData;
      if (!qcData.tradeType) {
        throw new Error('Quick contact requires tradeType');
      }
      subject = templates.quickContact.subject(qcData);
      body = templates.quickContact.body(qcData);
      break;
    }

    default:
      throw new Error(`Unhandled template type: ${templateType}`);
  }

  // Validate no placeholders remain
  validateNoPlaceholders(subject, `${templateType}.subject`);
  validateNoPlaceholders(body, `${templateType}.body`);

  // Format postcode to uppercase in the final output
  body = body.replace(new RegExp(data.postcode, 'gi'), data.postcode.toUpperCase());

  return { subject, body };
}

// Helper to check if a message has placeholders (for UI feedback)
export function hasPlaceholders(text: string): boolean {
  return PLACEHOLDER_REGEX.test(text);
}

// Helper to highlight placeholders in text (for UI display)
export function highlightPlaceholders(text: string): string {
  return text.replace(PLACEHOLDER_REGEX, '<span class="text-red-500 font-semibold">$&</span>');
}

// Helper to extract placeholder names from text
export function extractPlaceholders(text: string): string[] {
  const matches = text.match(PLACEHOLDER_REGEX);
  if (!matches) return [];
  return matches.map(match => match.slice(1, -1)); // Remove [ and ]
}
